/**
 * LocationFilters Component
 * Filter orders by delivery location (City and State)
 * Supports Indian cities and all states/UTs
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 4 (AC-6)
 */

import * as React from 'react';
import { MapPin, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// ============================================
// CONSTANTS - INDIAN LOCATIONS
// ============================================

/**
 * Major Indian cities (alphabetical)
 */
const INDIAN_CITIES = [
  'Ahmedabad',
  'Bangalore',
  'Bhopal',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Delhi',
  'Goa',
  'Gurgaon',
  'Hyderabad',
  'Indore',
  'Jaipur',
  'Kochi',
  'Kolkata',
  'Lucknow',
  'Mumbai',
  'Nagpur',
  'Pune',
  'Surat',
  'Vadodara',
  'Visakhapatnam',
] as const;

/**
 * Indian States (28 states)
 */
const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
] as const;

/**
 * Union Territories (8 UTs)
 */
const UNION_TERRITORIES = [
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const;

/**
 * Combined list of all states and UTs
 */
const ALL_STATES = [...INDIAN_STATES, ...UNION_TERRITORIES].sort();

// ============================================
// INTERFACE
// ============================================

export interface LocationFiltersProps {
  selectedCity: string | null;
  selectedState: string | null;
  onLocationChange: (city: string | null, state: string | null) => void;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const LocationFilters = React.memo(function LocationFilters({
  selectedCity,
  selectedState,
  onLocationChange,
  className,
}: LocationFiltersProps) {
  // Handle city change
  const handleCityChange = React.useCallback(
    (value: string) => {
      const newCity = value === 'ALL' ? null : value;
      onLocationChange(newCity, selectedState);
    },
    [onLocationChange, selectedState]
  );

  // Handle state change
  const handleStateChange = React.useCallback(
    (value: string) => {
      const newState = value === 'ALL' ? null : value;
      onLocationChange(selectedCity, newState);
    },
    [onLocationChange, selectedCity]
  );

  // Clear city filter
  const handleClearCity = React.useCallback(() => {
    onLocationChange(null, selectedState);
  }, [onLocationChange, selectedState]);

  // Clear state filter
  const handleClearState = React.useCallback(() => {
    onLocationChange(selectedCity, null);
  }, [onLocationChange, selectedCity]);

  // Clear all location filters
  const handleClearAll = React.useCallback(() => {
    onLocationChange(null, null);
  }, [onLocationChange]);

  // Check if any location filter is active
  const hasActiveFilters = selectedCity !== null || selectedState !== null;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Label className="text-sm font-medium text-foreground">
            Location
          </Label>
        </div>

        {/* Clear All Button */}
        {hasActiveFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="h-7 px-2 text-xs"
            aria-label="Clear all location filters"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Filter Inputs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* City Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="city-filter" className="text-xs text-muted-foreground">
              City
            </Label>
            {selectedCity && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearCity}
                className="h-5 px-1 text-xs"
                aria-label="Clear city filter"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <Select
            value={selectedCity || 'ALL'}
            onValueChange={handleCityChange}
          >
            <SelectTrigger
              id="city-filter"
              className="w-full"
              aria-label="Filter by city"
            >
              <SelectValue placeholder="All Cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Cities</SelectItem>
              {INDIAN_CITIES.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Selected City Badge */}
          {selectedCity && (
            <Badge
              variant="secondary"
              className="text-xs font-normal"
              aria-label={`Selected city: ${selectedCity}`}
            >
              {selectedCity}
            </Badge>
          )}
        </div>

        {/* State Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="state-filter" className="text-xs text-muted-foreground">
              State / UT
            </Label>
            {selectedState && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearState}
                className="h-5 px-1 text-xs"
                aria-label="Clear state filter"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <Select
            value={selectedState || 'ALL'}
            onValueChange={handleStateChange}
          >
            <SelectTrigger
              id="state-filter"
              className="w-full"
              aria-label="Filter by state or union territory"
            >
              <SelectValue placeholder="All States" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All States / UTs</SelectItem>
              {ALL_STATES.map((state) => (
                <SelectItem key={state} value={state}>
                  {state}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Selected State Badge */}
          {selectedState && (
            <Badge
              variant="secondary"
              className="text-xs font-normal"
              aria-label={`Selected state: ${selectedState}`}
            >
              {selectedState}
            </Badge>
          )}
        </div>
      </div>

      {/* Accessibility announcement */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {selectedCity && selectedState
          ? `Filtering by ${selectedCity}, ${selectedState}`
          : selectedCity
          ? `Filtering by ${selectedCity}`
          : selectedState
          ? `Filtering by ${selectedState}`
          : 'No location filters applied'}
      </div>
    </div>
  );
});
